// src/sim/ai/p4-caches.ts — Shared P4 round-lifecycle cache reset signal.
//
// Decouples the "new simulation run" signal from tick.ts so that world.ts can
// call resetP4CachesForNewSim() without creating a circular import
// (tick.ts → sim/world types, world.ts → tick.ts would create a cycle).
//
// Pattern: tick.ts registers its reset callbacks here at module load time.
// world.ts calls resetP4CachesForNewSim() at createInitialWorldState time.
//
// Source: .planning/phases/04-ai/04-10-PLAN.md Task 1 (determinism fix).

type ResetFn = () => void;

const _resetCallbacks: ResetFn[] = [];

/**
 * Register a P4 cache reset callback. Called by tick.ts once at module load.
 * Multiple callbacks are supported (one per cache type).
 */
export function registerP4ResetCallback(fn: ResetFn): void {
  _resetCallbacks.push(fn);
}

/**
 * Reset all registered P4 per-round caches. Call at the start of every new
 * simulation run (createInitialWorldState) to ensure replay-equality holds
 * even when the same rngSeed is used across multiple runs.
 *
 * Also called by tick.ts's round-started handler and tick===0 guard.
 */
export function resetP4CachesForNewSim(): void {
  for (const fn of _resetCallbacks) fn();
}
